import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserStatusEnum } from '@tourfold/libraries/tf-api-client-public-v2';
import { RolesService } from '../../roles/roles.service';
import { SkillsService } from '@tourfold/features/settings/skills';
import { TagsService } from '@tourfold/features/settings/tags';

export interface UserFiltersDialogData {
  statuses: string[];
  roleIds: string[];
  skillIds: string[];
  tagIds: string[];
}

export interface UserFiltersDialogResult {
  statuses: string[];
  roleIds: string[];
  skillIds: string[];
  skillLabels: Record<string, string>;
  tagIds: string[];
  tagLabels: Record<string, string>;
  tagColors: Record<string, string>;
}

@Component({
  selector: 'tf-settings-user-filters-dialog',
  imports: [FormsModule, MultiSelectModule, ButtonModule, TranslateModule],
  templateUrl: './user-filters-dialog.component.html',
  styleUrl: './user-filters-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFiltersDialogComponent implements OnInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(DynamicDialogConfig<UserFiltersDialogData>);
  private readonly rolesService = inject(RolesService);
  private readonly skillsService = inject(SkillsService);
  private readonly tagsService = inject(TagsService);
  private readonly translateService = inject(TranslateService);

  readonly selectedStatuses = signal<string[]>([]);
  readonly selectedRoleIds = signal<string[]>([]);
  readonly selectedSkillIds = signal<string[]>([]);
  readonly selectedTagIds = signal<string[]>([]);

  readonly roleOptions = signal<{ label: string; value: string }[]>([]);
  readonly skillOptions = signal<{ label: string; value: string }[]>([]);
  readonly tagOptions = signal<{ label: string; value: string; color: string }[]>([]);
  readonly loadingRoles = signal(false);
  readonly loadingSkills = signal(false);
  readonly loadingTags = signal(false);

  get statusOptions(): { label: string; value: UserStatusEnum }[] {
    return [
      {
        label: this.translateService.instant('SETTINGS_ACCESS.USERS.STATUS_ACTIVE'),
        value: UserStatusEnum.Active,
      },
      {
        label: this.translateService.instant('SETTINGS_ACCESS.USERS.STATUS_INACTIVE'),
        value: UserStatusEnum.Inactive,
      },
      {
        label: this.translateService.instant('SETTINGS_ACCESS.USERS.STATUS_BLOCKED'),
        value: UserStatusEnum.Blocked,
      },
    ];
  }

  ngOnInit(): void {
    const data = this.dialogConfig.data;
    if (data) {
      this.selectedStatuses.set([...data.statuses]);
      this.selectedRoleIds.set([...data.roleIds]);
      this.selectedSkillIds.set([...data.skillIds]);
      this.selectedTagIds.set([...(data.tagIds ?? [])]);
    }
    this.loadRoles();
    this.loadSkills();
    this.loadTags();
  }

  clearAll(): void {
    this.selectedStatuses.set([]);
    this.selectedRoleIds.set([]);
    this.selectedSkillIds.set([]);
    this.selectedTagIds.set([]);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  apply(): void {
    const skillLabels: Record<string, string> = {};
    this.selectedSkillIds().forEach((id) => {
      const option = this.skillOptions().find((o) => o.value === id);
      if (option) skillLabels[id] = option.label;
    });
    const tagLabels: Record<string, string> = {};
    const tagColors: Record<string, string> = {};
    this.selectedTagIds().forEach((id) => {
      const option = this.tagOptions().find((o) => o.value === id);
      if (option) {
        tagLabels[id] = option.label;
        tagColors[id] = option.color;
      }
    });
    const result: UserFiltersDialogResult = {
      statuses: this.selectedStatuses(),
      roleIds: this.selectedRoleIds(),
      skillIds: this.selectedSkillIds(),
      skillLabels,
      tagIds: this.selectedTagIds(),
      tagLabels,
      tagColors,
    };
    this.dialogRef.close(result);
  }

  private loadRoles(): void {
    this.loadingRoles.set(true);
    this.rolesService.getRoles().subscribe({
      next: (response) => {
        this.roleOptions.set(
          response.items.map((role) => ({
            label: this.formatRoleName(role.name),
            value: role.id,
          }))
        );
        this.loadingRoles.set(false);
      },
      error: () => this.loadingRoles.set(false),
    });
  }

  private loadSkills(): void {
    this.loadingSkills.set(true);
    this.skillsService.getSkillsData({ page: 0, size: 100 }).subscribe({
      next: (response) => {
        this.skillOptions.set(
          response.items.map((skill) => ({
            label: skill.name,
            value: skill.id,
          }))
        );
        this.loadingSkills.set(false);
      },
      error: () => this.loadingSkills.set(false),
    });
  }

  private loadTags(): void {
    this.loadingTags.set(true);
    this.tagsService.getTags().subscribe({
      next: (response) => {
        this.tagOptions.set(
          response.items
            .filter(
              (t): t is { id: string; name: string; color: string } =>
                !!t.id && !!t.name && !!t.color
            )
            .map((t) => ({ label: t.name, value: t.id, color: t.color }))
        );
        this.loadingTags.set(false);
      },
      error: () => this.loadingTags.set(false),
    });
  }

  private formatRoleName(roleName: string): string {
    return roleName
      .split(/[_\s]+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
}
